<template>
  <div>
    <template v-if="cardEditable || transfer.airline_card">
      <q-input
        :model-value="transfer.airline_card"
        :type="domain.airlineCard.type || 'number'"
        :label="$t(`label.airline_card.${domain.name}.title`)"
        :readonly="!cardEditable"
        :rules="airlineCardRules"
        hide-bottom-space
        lazy-rules
        outlined
        @update:modelValue="$emit('update-value', 'airline_card', $event)"
      >
        <template #prepend>
          <q-icon
            name="img:common/icons/hash.svg"
            size="24px"
          />
        </template>
      </q-input>
      <div
        v-if="domain.airlineCard.text"
        class="fs-12"
      >
        {{ airlineCardText }}
        <q-icon
          v-if="domain.is.aeroflot"
          name="info"
          color="black"
          class="cursor-pointer"
          @click="bus.emit('show-aeroflot-modal')"
        />
      </div>
      <div
        v-if="domain.is.vistara"
        class="vistara-link fs-14"
        v-html="$t('label.airline_card.vistara.link', {
          link: 'https://www.airvistara.com/in/en/club-vistara/register',
        })"
      />
    </template>
    <q-input
      v-if="showNameField"
      ref="airlineCardNameRef"
      class="q-mt-12"
      :model-value="transfer.airline_card_name"
      :label="$t(`label.airline_card.${domain.name}.first_name`)"
      :disable="!transfer.airline_card"
      :readonly="!nameEditable"
      :rules="airlineCardNameRules"
      lazy-rules
      hide-bottom-space
      outlined
      @update:modelValue="updateName"
    >
      <template #prepend>
        <q-icon
          name="person"
          size="24px"
          color="black"
        />
      </template>
    </q-input>
    <q-input
      v-if="showSurnameField"
      ref="airlineCardSurnameRef"
      class="q-mt-12"
      :model-value="transfer.airline_card_surname"
      :label="$t(`label.airline_card.${domain.name}.surname`)"
      :disable="!transfer.airline_card"
      :readonly="!surnameEditable"
      :rules="airlineCardNameRules"
      lazy-rules
      hide-bottom-space
      outlined
      @update:modelValue="$emit('update-value', 'airline_card_surname', $event.toUpperCase())"
    >
      <template #prepend>
        <q-icon
          name="person"
          size="24px"
          color="black"
        />
      </template>
    </q-input>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  inject,
} from 'vue';

import { useI18n } from 'vue-i18n';

import useRequiredFieldRules from '~composables/useRequiredFieldRules';
import useAeroflotSpecialPromo from '~composables/useAeroflotSpecialPromo';
import useVistaraSpecialPromo from '~composables/useVistaraSpecialPromo';

const { t } = useI18n();

const { requiredField } = useRequiredFieldRules();
const { aeroflotMilesCount } = useAeroflotSpecialPromo();
const { vistaraMilesCount } = useVistaraSpecialPromo();

const props = defineProps({
  transfer: {
    type: Object,
    required: true,
  },
  cardEditable: {
    type: Boolean,
    default: true,
  },
  nameEditable: {
    type: Boolean,
    default: true,
  },
  surnameEditable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update-value']);

const bus = inject('bus');
const moment = inject('moment');
const domain = inject('domain');

const airlineCardNameRef = ref(null);
const airlineCardSurnameRef = ref(null);

function onlyLatinCharacters(value) {
  if (value && domain.airlineCard.onlyLatin) {
    return !(/[^A-Z\s]/.test(value)) || t('error.use_only_latin');
  }
  return true;
}

function updateName(value) {
  if (domain.is.royalskies && value) {
    value = value.at(-1);
  }
  emit('update-value', 'airline_card_name', value.toUpperCase());
}

const airlineCardRules = computed(() => (domain.is.suma ? [(v) => requiredField(v)] : []));

const airlineCardNameRules = computed(() => (props.transfer.airline_card
  ? [onlyLatinCharacters, (v) => requiredField(v)]
  : []));

const airlineCardText = computed(() => {
  if (domain.is.aeroflot) {
    return t(`label.airline_card.aeroflot.text_${aeroflotMilesCount.value}`);
  }

  if (domain.is.vistara) {
    return t('label.airline_card.vistara.text', { miles: vistaraMilesCount.value });
  }

  if (domain.is.royalskies) {
    const isBonusTime = moment().isBetween('2024-04-21T00:00:00+0000', '2024-05-31T23:59:59+0000');
    return t('label.airline_card.royalskies.text', { miles: isBonusTime ? 12 : 4 });
  }

  if (domain.is.suma) {
    const isBonusTime = moment().isBetween('2024-05-01T00:00:00+0000', '2024-05-31T23:59:59+0000');
    return t('label.airline_card.suma.text', { miles: isBonusTime ? 15 : 10 });
  }

  if (domain.is.saav) {
    const isBonusTime = moment().isBetween('2024-03-31T00:00:00+0000', '2024-05-31T23:59:59+0000');
    return t('label.airline_card.saav.text', { miles: isBonusTime ? 14 : 7 });
  }

  return t(`label.airline_card.${domain.name}.text`);
});

const showNameField = computed(() => {
  if (!domain.airlineCard.name) return false;
  return props.nameEditable || props.transfer.airline_card_name;
});

const showSurnameField = computed(() => {
  if (!domain.airlineCard.surname) return false;
  return props.surnameEditable || props.transfer.airline_card_surname;
});

watch(() => props.transfer.airline_card, (value) => {
  if (!value) {
    airlineCardNameRef.value?.resetValidation();
    airlineCardSurnameRef.value?.resetValidation();

    emit('update-value', 'airline_card_name', '');
    emit('update-value', 'airline_card_surname', '');
  }
});
</script>
